'use server';

import { SessionService } from '@/contexts/authentication';

/**
 * A single generated reference image of a character as it appears inside one
 * book/story. Powers the "Appearance across stories" section in the character
 * edit form — lets the user reuse a per-book look as the character's primary
 * portrait.
 */
export interface CharacterAppearance {
  bookId: string;
  bookTitle: string;
  style: string;
  imageUrl: string;
}

export async function getCharacterAppearances(
  characterId: string
): Promise<CharacterAppearance[]> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];

  const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
  const books = await getIllustrationRepository().listByFamily(familyId, { limit: 500 });

  const appearances: CharacterAppearance[] = [];
  const seen = new Set<string>();

  for (const book of books) {
    for (const ref of book.characterReferences ?? []) {
      // an outfit variant («Варя — пижама») is one book's costume sheet, not an appearance
      if (ref.characterId !== characterId || !ref.generatedReferenceUrl || ref.variantOf) continue;
      if (seen.has(ref.generatedReferenceUrl)) continue;
      seen.add(ref.generatedReferenceUrl);
      appearances.push({
        bookId: book.id,
        bookTitle: book.title,
        style: book.style,
        imageUrl: ref.generatedReferenceUrl,
      });
    }
  }

  return appearances;
}
